import { useState, useEffect, useCallback } from "react";
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { 
  Activity, 
  Download, 
  Search, 
  RefreshCw,
  LogIn,
  LogOut,
  Eye,
  FileText,
  Shield,
  Database,
  Settings,
  ChevronLeft,
  ChevronRight,
  ClipboardList
} from "lucide-react";
import { BACKEND_URL } from '../config/api';
import axios from "axios";
import { toast } from "sonner";

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Fetcher for SWR
const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  // Debounce search input (300ms)
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Build query params
  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: (page * limit).toString()
    });
    
    if (actionFilter !== "all") {
      params.append("action", actionFilter);
    }
    if (debouncedSearch) {
      params.append("username", debouncedSearch);
    }
    
    return params.toString();
  }, [page, actionFilter, debouncedSearch]);

  // SWR for data fetching with revalidation
  const { data, error, isLoading, mutate } = useSWR(
    `${BACKEND_URL}/api/v2/super-admin/activity-logs?${buildQueryString()}`,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000, // Auto-refresh every 60s (was 30s)
      dedupingInterval: 10000, // Dedupe requests within 10 seconds
      onError: (err) => {
        console.error("Error fetching activity logs:", err);
        toast.error("Failed to load activity logs");
      }
    }
  );

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [actionFilter, debouncedSearch]);

  const exportLogs = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("jwtToken");
      
      if (format === 'csv') {
        const response = await axios.get(
          `${BACKEND_URL}/api/v2/super-admin/activity-logs/export?format=csv`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Activity logs exported as CSV");
      } else {
        const response = await axios.get(
          `${BACKEND_URL}/api/v2/super-admin/activity-logs/export?format=json`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const blob = new Blob([JSON.stringify(response.data.logs, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Activity logs exported as JSON");
      }
    } catch (error) {
      console.error("Error exporting logs:", error);
      toast.error("Failed to export logs");
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'LOGIN_SUCCESS': <LogIn className="w-4 h-4 text-green-600" />,
      '2FA_REQUESTED': <Shield className="w-4 h-4 text-blue-600" />,
      '2FA_VERIFIED': <Shield className="w-4 h-4 text-green-600" />,
      'LOGIN_FAILED': <Shield className="w-4 h-4 text-red-600" />,
      '2FA_FAILED': <Shield className="w-4 h-4 text-red-600" />,
      '2FA_BLOCKED': <Shield className="w-4 h-4 text-red-600" />,
      'LOGOUT': <LogOut className="w-4 h-4 text-gray-600" />,
      'EXPORT_ACTIVITY_LOGS': <Download className="w-4 h-4 text-blue-600" />,
      'EXPORT_DASHBOARD': <Download className="w-4 h-4 text-blue-600" />,
      'EXPORT_ACCOUNTS': <Download className="w-4 h-4 text-blue-600" />,
      'ORG_APPROVED': <Database className="w-4 h-4 text-green-600" />,
      'ORG_SUSPENDED': <Database className="w-4 h-4 text-red-600" />,
      'ORG_CREATED': <Database className="w-4 h-4 text-purple-600" />,
      'CREDITS_ADDED': <Settings className="w-4 h-4 text-amber-600" />,
      'CREDITS_REMOVED': <Settings className="w-4 h-4 text-amber-600" />,
      'VIEW_ORG': <Eye className="w-4 h-4 text-gray-500" />,
      'VIEW_DASHBOARD': <Eye className="w-4 h-4 text-gray-500" />,
    };
    return iconMap[action] || <Activity className="w-4 h-4 text-gray-400" />;
  };

  const getActionBadgeColor = (action) => {
    if (action?.includes('SUCCESS') || action?.includes('VERIFIED') || action?.includes('APPROVED')) {
      return 'bg-green-100 text-green-700';
    }
    if (action?.includes('FAILED') || action?.includes('BLOCKED') || action?.includes('SUSPENDED')) {
      return 'bg-red-100 text-red-700';
    }
    if (action?.includes('EXPORT') || action?.includes('2FA_REQUESTED')) {
      return 'bg-blue-100 text-blue-700';
    }
    if (action?.includes('CREDITS')) {
      return 'bg-amber-100 text-amber-700';
    }
    if (action?.includes('ORG')) {
      return 'bg-purple-100 text-purple-700';
    }
    return 'bg-gray-100 text-gray-600';
  };

  const getShortActionLabel = (action) => {
    const labels = {
      'LOGIN_SUCCESS': 'Login',
      '2FA_REQUESTED': '2FA Sent',
      '2FA_VERIFIED': '2FA OK',
      'LOGIN_FAILED': 'Failed',
      '2FA_FAILED': '2FA Fail',
      '2FA_BLOCKED': 'Blocked',
      'LOGOUT': 'Logout',
      'EXPORT_ACTIVITY_LOGS': 'Export',
      'EXPORT_DASHBOARD': 'Export',
      'ORG_APPROVED': 'Approved',
      'ORG_SUSPENDED': 'Suspended',
      'ORG_CREATED': 'Created',
      'CREDITS_ADDED': '+Credits',
      'CREDITS_REMOVED': '-Credits',
      'VIEW_ORG': 'Viewed',
      'VIEW_DASHBOARD': 'Viewed',
    };
    return labels[action] || action?.replace(/_/g, ' ') || 'Unknown';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const actionTypes = [
    { value: 'all', label: 'All' },
    { value: 'LOGIN_SUCCESS', label: 'Logins' },
    { value: '2FA', label: '2FA' },
    { value: 'EXPORT', label: 'Exports' },
    { value: 'ORG', label: 'Orgs' },
    { value: 'CREDITS', label: 'Credits' },
  ];

  return (
    <TabPageLayout
      title="Activity Log"
      subtitle={`${total.toLocaleString()} total events • Auto-refreshes every 30s`}
      actions={
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => mutate()}
            className="h-9"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportLogs('csv')}
            disabled={exporting}
            className="h-9"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportLogs('json')}
            disabled={exporting}
            className="h-9"
          >
            <FileText className="w-4 h-4 mr-1" />
            JSON
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters */}
        <DataCard>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            
            {/* Action Type Filters */}
            <div className="flex gap-2 flex-wrap">
              {actionTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setActionFilter(type.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    actionFilter === type.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </DataCard>

        {/* Logs Table */}
        <DataCard>
          {isLoading ? (
            <PageLoader minHeight="h-48" />
          ) : error ? (
            <EmptyState
              icon={<Activity />}
              title="Failed to load logs"
              description="There was an error loading activity logs. Please try again."
              action={
                <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              }
            />
          ) : logs.length === 0 ? (
            <EmptyState
              icon={<ClipboardList />}
              title="No activity logs"
              description={searchTerm || actionFilter !== 'all' 
                ? "No logs match your current filters. Try adjusting your search."
                : "Activity logs will appear here as events occur."
              }
            />
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">User</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">IP Address</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log, index) => (
                      <tr key={log.id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getActionBadgeColor(log.action)}`}>
                              {getShortActionLabel(log.action)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {log.username || 'system'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {log.ipAddress || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">
                          {log.details ? JSON.stringify(log.details).slice(0, 50) + (JSON.stringify(log.details).length > 50 ? '...' : '') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200 mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {(page * limit + 1).toLocaleString()} - {Math.min((page + 1) * limit, total).toLocaleString()} of {total.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 min-w-[100px] text-center">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="h-9"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default ActivityLog;
